import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { allSharedComponents } from "../../../../../shared";
import { instructorActions } from "../../../../../redux/actions";
import {
  changeTableSortIcons,
  copyToClipboard,
  getSortDirectionProperty,
} from "../../../../../system";
import {
  emptyString,
  TableColumnStyle,
  translationConstants,
} from "../../../../../constants";
import {
  getDataToExport,
  getExportHeaders,
  getInstructorType,
} from "../utilities";
import Tooltip from "@mui/material/Tooltip";
import FilterComponent from "../FilterComponent";
import ViewInstructorApproval from "../Modals/ViewInstructorApproval";

const defaultQueryExpression = "id != null";
const {
  DataTable,
  RejectedCertificate,
  ApprovedCertificate,
  CustomModal,
  ExportComponent,
  ModalApprovedRejected,
} = allSharedComponents;

const initialGridState = {
  length: 10,
  search: "",
  pageNumber: 1,
  orderBy: null,
  filterQuery: defaultQueryExpression,
};

const InstructorsApprovalsTab = () => {
  const dispatch = useDispatch();

  const sortingInProgressRef = useRef(false);

  const [dataToExport, setDataToExport] = useState([]);
  const [columnsToExport, setColumnsToExport] = useState([]);
  const [modalContent, setModalContent] = useState(null); // State to track modal content
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(gridState.pageNumber);

  const openAppRejModal = useSelector(
    (state) => state.UserReducer.openAppRejModal
  );

  const { userEmail, userID, fileDetails, eventType, modalStatus } =
    openAppRejModal;

  const approvalList = useSelector(
    (state) => state.InstructorReducer.instructorRequestedApprovalsList
  );
  const approvalListPagination = useSelector(
    (state) => state.InstructorReducer.instructorRequestedApprovalsPagination
  );
  const isShow = useSelector(
    (state) => state.InstructorReducer.isApprovalModalOpen
  );

  const onSetShow = () => {
    dispatch(instructorActions.toggleApprovalModal(!isShow));
  };

  const getRowData = (data) => {
    dispatch(
      instructorActions.getApprovalRequestedInstructorById(data?.userInfo.id)
    );
    dispatch(instructorActions.toggleApprovalModal(true));
  };

  const getcolumns = () => [
    {
      id: "id",
      accessor: "userInfo.id",
      name: t(translationConstants.RECORD_ID),
      cell: (row) => (
        <Tooltip
          title={<div>{t(translationConstants.COPY_TO_CLIPBOARD)}</div>}
          placement="top"
          arrow
        >
          <div
            className="truncate-text cursor-pointer"
            onClick={() => copyToClipboard(row.userInfo.id)}
          >
            {row.userInfo.id}
          </div>
        </Tooltip>
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "englishFirstName",
      reorder: true,
      sortable: true,
      sortDirection: null,
      accessor: "fullName",
      name: t(translationConstants.fullName),
      selector: (row) =>
        `${row.userInfo.englishFirstName} ${row.userInfo.englishLastName}`,
    },
    {
      id: "arabicFirstName",
      accessor: "arabicFullName",
      name: t(translationConstants.nameArabic),
      selector: (row) =>
        `${row.userInfo.arabicFirstName || ""} ${
          row.userInfo.arabicLastName || ""
        }`,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "140px",
    },
    {
      id: "email",
      accessor: "userInfo.email",
      name: t(translationConstants.Email),
      selector: (row) => row.userInfo.email,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "centerName",
      accessor: "centerName",
      name: t(translationConstants.centerName),
      selector: (row) => row.centerName,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "150px",
      cell: (row) => <div style={{ minWidth: "150px" }}>{row.centerName}</div>,
    },
    {
      id: "noOfHours",
      accessor: "noOfHours",
      name: t(translationConstants.noOfHours),
      selector: (row) => row.noOfHours,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "140px",
      cell: (row) => <div style={{ minWidth: "140px" }}>{row.noOfHours}</div>,
    },
    {
      id: "noOfStudents",
      accessor: "noOfStudents",
      name: t(translationConstants.noOfStudents),
      selector: (row) => row.noOfStudents,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "160px",
      cell: (row) => (
        <div style={{ minWidth: "160px" }}>{row.noOfStudents}</div>
      ),
    },
    {
      id: "type",
      accessor: "type",
      name: t(translationConstants.instructorType),
      selector: (row) => row.type,
      reorder: true,
      sortable: true,
      sortDirection: null,
      cell: (row) => t(row.type),
    },
    {
      id: "actions",
      name: t(translationConstants.Actions),
      cell: (row) => (
        <svg
          onClick={() => getRowData(row)}
          className="table-action-btn"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z"
            stroke="#48722D"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="#48722D"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ];

  const { t, i18n } = useTranslation();
  const [rowData, setRowData] = useState(emptyString);
  const [columns, setColumns] = useState(getcolumns());

  const HandleModal = () => {
    if (eventType === "rejected") {
      setModalContent(
        <RejectedCertificate
          userEmail={userEmail}
          userID={userID}
          fileDetails={fileDetails}
          isInstructor={true}
          gridState={gridState}
        />
      );
    } else if (eventType === "approved") {
      setModalContent(
        <ApprovedCertificate
          userEmail={userEmail}
          userID={userID}
          fileDetails={fileDetails}
          isInstructor={true}
          gridState={gridState}
        />
      );
    }
  };

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    setColumns(changeTableSortIcons(columns, sortedColumn));
    const columnDirection = getSortDirectionProperty(sortedColumn);
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy: columnDirection ? `${sortedColumn.id} ${columnDirection}` : null,
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  useEffect(() => {
    HandleModal();
    // eslint-disable-next-line
  }, [approvalList, openAppRejModal]);

  useEffect(() => {
    dispatch(instructorActions.getApprovalRequestedInstructors(gridState));
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    setRowData(approvalList);
    setDataToExport(getDataToExport(approvalList));
    // eslint-disable-next-line
  }, [approvalList]);

  useEffect(() => {
    setColumns(getcolumns());
    // eslint-disable-next-line
  }, [i18n.dir()]);

  useEffect(() => {
    setColumnsToExport(getExportHeaders(columns));
  }, [columns]);

  const exportData = () => {
    let dataToSend = {
      ...gridState,
      length: 0,
      orderBy: gridState.orderBy,
    };
    dispatch(instructorActions.exportInstructorData(dataToSend, 1));
  };

  return (
    <div className="tableContainer">
      <FilterComponent
        gridState={gridState}
        setGridState={setGridState}
        defaultQueryExpression={defaultQueryExpression}
      >
        <ExportComponent
          isServerSide
          onClick={exportData}
          filename={t(translationConstants.approval)}
        />
      </FilterComponent>
      <DataTable
        columns={columns}
        items={rowData}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={approvalListPagination?.totalElements}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={defaultPage}
        paginationResetDefaultPage={true}
      />
      <CustomModal
        scrollable={true}
        size="lg"
        setShow={onSetShow}
        show={isShow}
        customClass="view-user-modal"
      >
        <ViewInstructorApproval
          setShow={(flag) =>
            dispatch(instructorActions.toggleApprovalModal(flag))
          }
        />
      </CustomModal>

      {openAppRejModal.modalStatus && (
        <ModalApprovedRejected
          scrollable={true}
          size="lg"
          show={modalStatus}
          customClass="view-user-modal"
        >
          {modalContent}
        </ModalApprovedRejected>
      )}
    </div>
  );
};

export default InstructorsApprovalsTab;
